// R2/G9, ported (WS-D §6) — what an operator with no visible console section is TOLD.
//
// Copied from `site-admin-ui/src/shell/ZeroAccessPanel.tsx` (D-16: a copy into a new package,
// never a shared import). The five-state structure, the branch ORDERING and the
// `aria-live`-on-every-state comment are byte-identical because each was earned. ONE branch's
// COPY changes — see the `resource-only` branch below — and that change is required, not
// cosmetic.
//
// The six states (the five of the copy, plus the inactive-role state of plan
// 2026-10-22-cp-active-role) stay apart because collapsing them re-creates the defect this
// panel exists to delete: an unresolved or failed access lookup rendering as a DENIAL (D-10).
// "We could not determine your access" is true; "you have no access" would be a guess wearing
// the costume of an answer.
import { AlertTriangle, KeyRound, ListX, ShieldOff } from 'lucide-react';
import type { ResourceGrantSnapshot } from '@zion/sdk-react';
import { Button } from './Button';
import { useActiveRole } from './active-role-context';
import { roleName } from './role-names';

/**
 * Why `resourceGrants` is unusable. Discriminated rather than a bare string because the two
 * origins have DIFFERENT REMEDIES — a failed fetch is worth retrying, a truncated expansion is
 * not (a platform admin must trim grants) — and a panel that string-matched an error message
 * to tell them apart would be one copy edit away from silently collapsing the two.
 */
export interface GrantsError {
  readonly kind: 'fetch' | 'truncated';
  readonly message: string;
  /**
   * WHAT failed, so the card can lead with it. Defaults to `'access'`.
   *
   * ⛔ A separate axis from `kind`, deliberately. `kind` selects the REMEDY (retry vs ask an admin to
   * trim grants); `subject` selects the SENTENCE. Collapsing them would mean adding a `kind` per
   * subject and re-deriving the remedy each time.
   *
   * ⚠️ It exists because the app-list fetch reuses this card, and *"We couldn't determine your
   * access"* names the wrong thing when it was the catalog that 503'd. The operator's next question
   * is "what broke?", and the title is where they look.
   */
  readonly subject?: 'access' | 'apps';
}

export interface ZeroAccessPanelProps {
  /** Signed-in operator's email, for "you are signed in as X". `null` when the token carried none. */
  readonly email: string | null;
  /**
   * The caller's expanded resource grants. `null` means NOT YET KNOWN (still in flight) or
   * unusable — never "none". `[]` means loaded, genuinely none.
   */
  readonly resourceGrants: readonly ResourceGrantSnapshot[] | null;
  /** Non-null iff `resourceGrants` is null for a REASON rather than because it has not arrived. */
  readonly grantsError: GrantsError | null;
  /** Sign out. On this screen it is the operator's only action, beside "Switch role" in the inactive-role state. */
  readonly onSignOut: () => void;
}

/**
 * Shared card chrome — ONE box for all six states, so resolving from "checking" to an answer
 * swaps text inside a stable frame instead of moving the frame.
 *
 * The identity line and the sign-out button live here, which means they render in EVERY state,
 * deliberately:
 *   · Sign-out is present even while checking, because the fetch can hang and a control that
 *     appears only once the answer arrives would (a) leave a stuck operator with no action at
 *     all and (b) shift the layout when it pops in.
 *   · "Signed in as …" is the field that lets someone recognise the most likely real cause of
 *     being here — that they signed in with the wrong account.
 */
function Card({
  testId,
  icon,
  title,
  body,
  tone,
  email,
  onSignOut,
  alert = false,
}: {
  readonly testId: string;
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly body: React.ReactNode;
  readonly tone: string;
  readonly email: string | null;
  readonly onSignOut: () => void;
  readonly alert?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex h-full items-center justify-center p-8">
      {/*
        ⚠️ `aria-live` is present in EVERY state, `polite` for the calm ones. It is not
        decoration and it is not interchangeable with `role="alert"` alone.

        All six branches return `<Card>` at the same position, so React reconciles this
        `<div>` IN PLACE: on a pending → error transition the `role` attribute goes absent →
        `"alert"` at the very moment the content changes. A live region that is CREATED and
        POPULATED in one step is the documented case where assistive tech does NOT announce —
        the region has to exist before the mutation for the mutation to be observed. So the two
        error states, which are precisely the ones a stuck operator reaches, would have gone
        unannounced.

        With `aria-live` on every state the region already exists, and the escalation to
        `assertive` lands ON a live region rather than creating one. `aria-atomic` makes the
        whole card read as one message instead of only the changed text node.

        A test asserting `toHaveAttribute('role', 'alert')` proves PRESENCE, which was already
        true; it cannot prove announceability. Hence the attribute, not just the assertion.
      */}
      <div
        data-testid={testId}
        role={alert ? 'alert' : undefined}
        aria-live={alert ? 'assertive' : 'polite'}
        aria-atomic="true"
        className={`flex max-w-lg flex-col items-center gap-3 rounded-xl border ${tone} bg-surface p-10 text-center`}
      >
        {icon}
        <h1 className="text-sm font-medium text-primary">{title}</h1>
        {/*
          ⛔ `max-w-[52ch]`, NOT `max-w-md` — a UNIT conversion, not a width change. `max-w-md` is
          28rem = 448px, and measured in Chrome at this element's `text-xs`, `ch` = 8.668px ⇒ 448px
          IS 52ch. It renders 2.7px wider and is otherwise identical today.
          ⭐ The point is what happens LATER: a px measure silently changes its effective CHARACTER
          count every time the type ladder moves (the 2026-08-06 raise did exactly that), while `ch`
          is font-relative and holds the same line length at every rung. This one container bounds
          the `body` of all three zero-access cards, so it is where the measure belongs.
        */}
        <div className="max-w-[52ch] text-xs text-muted">{body}</div>
        {email === null ? null : (
          <p className="text-xs text-muted">
            Signed in as <span className="font-mono text-secondary">{email}</span>
          </p>
        )}
        {/* Secondary action, sized as a real target rather than a text link (Fitts). */}
        <Button data-testid="zero-access-sign-out" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </div>
  );
}

export function ZeroAccessPanel({
  email,
  resourceGrants,
  grantsError,
  onSignOut,
}: ZeroAccessPanelProps): React.JSX.Element {
  const activeRole = useActiveRole();
  const inactiveRoles = activeRole === null ? [] : activeRole.heldSiteRoles.filter((role) => role !== activeRole.activeRole);
  // ── Order is load-bearing: every ERROR state is checked before any state that could be read
  // as a denial. Exactly one branch returns, so exactly one panel can ever be in the document.

  // Truncated. A WARNING, not a critical error, and deliberately a different icon + colour from
  // the fetch failure: the response arrived intact, it was simply too large to evaluate. No
  // "try again" — retrying is futile, and offering it would be a lie.
  if (grantsError?.kind === 'truncated') {
    return (
      <Card
        testId="zero-access-truncated"
        alert
        tone="border-state-warning/30"
        icon={<ListX className="h-6 w-6 text-state-warning" aria-hidden />}
        title="Your access list is too large to evaluate"
        body={
          <>
            <p>
              You hold more resource grants than the console can expand at once, so we can&apos;t
              safely tell you what you have access to. A platform admin needs to reduce the number
              of grants on your account — replacing per-resource grants with a role is the usual
              fix.
            </p>
            <p className="mt-2 font-mono text-2xs text-muted">{grantsError.message}</p>
          </>
        }
        email={email}
        onSignOut={onSignOut}
      />
    );
  }

  // Fetch failed. An errorstate, never a denial — we do not know what this principal has, and
  // saying "you have none" would be inventing an answer.
  if (grantsError) {
    const isApps = grantsError.subject === 'apps';
    return (
      <Card
        testId="zero-access-error"
        alert
        tone="border-state-critical/30"
        icon={<AlertTriangle className="h-6 w-6 text-state-critical" aria-hidden />}
        // ⛔ LEAD WITH WHAT FAILED. The card is shared, the sentence is not.
        title={isApps ? "We couldn't load your apps" : "We couldn't determine your access"}
        body={
          <>
            <p>
              {isApps
                ? 'You are signed in, but the list of apps you can reach did not load — so we cannot show you anything yet. Try reloading. If it keeps happening, contact a platform admin.'
                : 'Your permissions loaded, but the check for resource-level access failed — so this page may be incomplete. Try reloading. If it keeps happening, contact a platform admin.'}
            </p>
            <p className="mt-2 font-mono text-2xs text-muted">{grantsError.message}</p>
          </>
        }
        email={email}
        onSignOut={onSignOut}
      />
    );
  }

  // Still in flight. A skeleton in the SAME box the answer will occupy, so nothing moves when it
  // arrives — and never the word "Loading".
  if (resourceGrants === null) {
    return (
      <Card
        testId="zero-access-pending"
        tone="border-border-subtle"
        icon={<KeyRound className="h-6 w-6 text-muted" aria-hidden />}
        title="Checking your access…"
        body={
          <span
            className="mt-1 block h-3 w-64 animate-pulse rounded bg-surface-muted motion-reduce:animate-none"
            aria-hidden
          />
        }
        email={email}
        onSignOut={onSignOut}
      />
    );
  }

  /*
   * Loaded, and the token names a held site role that is not active (plan 2026-10-22-cp-active-role):
   * either no role is active, or the active role opens no section. The answer is a switch, not a
   * request to an admin. It names the resource grants too, so this card hides no fact.
   */
  if (activeRole !== null && inactiveRoles.length > 0) {
    const acting = activeRole.activeRole === null ? null : roleName(activeRole.activeRole);
    return (
      <Card
        testId="zero-access-inactive-role"
        tone="border-border-subtle"
        icon={<KeyRound className="h-6 w-6 text-muted" aria-hidden />}
        title={acting === null ? 'Your site role is not active' : `${acting} opens no section`}
        body={
          <>
            <p className="max-w-[100ch] break-words">
              {acting === null ? 'No site role is active, so no section opens.' : `You act as ${acting}, and it opens no section.`} You hold{' '}
              {inactiveRoles.map(roleName).join(', ')}. Switch your active role in the account menu to open its sections.
            </p>
            {resourceGrants.length > 0 && (
              <p className="mt-2 max-w-[100ch]" data-testid="zero-access-inactive-role-grants">
                You also have access to {resourceGrants.length === 1 ? 'a resource' : 'resources'} directly, which
                doesn&apos;t open a section in this console.
              </p>
            )}
            <div className="mt-3 flex justify-center">
              <Button tone="primary" size="md" onClick={activeRole.requestRoleMenu} data-testid="zero-access-switch-role">
                Switch role
              </Button>
            </div>
          </>
        }
        email={email}
        onSignOut={onSignOut}
      />
    );
  }

  // Loaded, and there ARE grants — just no console section that surfaces them.
  //
  // ⚠️ THIS BRANCH'S REACHABILITY CHANGED IN R6, and the copy had to change with it. It was
  // reachable for APP grants in the old shell; under D-10 an app grant now confers the Apps
  // section, so here it is reachable ONLY for grants of `resourceType` `'tenant'` or `'dp'` —
  // which is correct, because this shell has no Platform or DP section (D-14).
  //
  // ⛔ The old copy said the grant "doesn't YET open a section", which is now factually wrong
  // for the one population this branch serves: for them it will not open one at all in this
  // console, while app grants do. A test reaching this branch with an APP grant will find the
  // panel not rendered, and the natural "fix" is to delete the branch — which would then
  // silently mis-tell a DP-granted operator they have nothing.
  if (resourceGrants.length > 0) {
    return (
      <Card
        testId="zero-access-resource-only"
        tone="border-state-info/30"
        icon={<KeyRound className="h-6 w-6 text-state-info" aria-hidden />}
        title="You have access to specific resources, but no console sections yet"
        body={
          <p>
            You&apos;ve been granted access to{' '}
            {resourceGrants.length === 1 ? 'a resource' : 'resources'} directly, which doesn&apos;t
            open a section in this console. App grants do — ask a platform admin if you expected an
            app here.
          </p>
        }
        email={email}
        onSignOut={onSignOut}
      />
    );
  }

  // Loaded, genuinely nothing. Calm and informational — NOT an error. This is the normal state of
  // a freshly-created operator, and colouring it red would train people to read "not provisioned
  // yet" as "something is broken".
  return (
    <Card
      testId="zero-access-panel"
      tone="border-border-subtle"
      icon={<ShieldOff className="h-6 w-6 text-state-info" aria-hidden />}
      title="You don't have access to any sections yet"
      body={
        <p>
          Your account is active, but no roles have been assigned to it — so there&apos;s nothing to
          show here. Ask a platform admin to assign you a role.
        </p>
      }
      email={email}
      onSignOut={onSignOut}
    />
  );
}
